// Paywall modal — listens to the global paywall event bus
// (`subscribePaywall` in lib/paywall.ts) and renders an upgrade prompt
// whenever any API call comes back with
// `402 { code: "entitlement_required", slug, paywallEnabled }`.
//
// Behaviour:
//   - paywallEnabled === true  → primary CTA navigates to /pricing
//   - paywallEnabled === false → primary CTA shows the "Coming soon" copy
//                                (checkout.comingSoonCta) and is disabled
//   - Esc / overlay click / "Maybe later" closes the modal
//   - Focus trap + aria-labelledby/aria-describedby provided by the
//     underlying Radix Dialog primitive
//
// Mounted once at the App root so any feature in the app can trigger
// it without prop drilling.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { subscribePaywall, type PaywallPayload } from "@/lib/paywall";
import { useIsChildPlayer } from "@/hooks/useIsChildPlayer";

export function PaywallModal() {
  // Native builds (Capacitor iOS / Android) ship without Stripe-backed
  // upgrade prompts so the binary complies with Apple App Store §3.1.1.
  // `__NATIVE_BUILD__` is inlined by Vite (`define` in vite.config.ts),
  // so the early return becomes `if (true) return null;` in the native
  // bundle and rollup tree-shakes the rest of this component out.
  if (__NATIVE_BUILD__) return null;
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [payload, setPayload] = useState<PaywallPayload | null>(null);
  // Task #156 — Family Safety + Kid Mode v1. A child profile must NEVER
  // be shown the upgrade prompt: every billing surface is for the host.
  // The 402 still fires from the server (defence in depth) but we drop
  // it on the floor here so the modal never opens for a child session.
  // We also subscribe only after session bootstrap completes (`isLoading
  // === false`) so a paywall event fired during bootstrap can't slip in
  // before the child predicate resolves; and if a child flip happens
  // while the modal is already open (host demoted them mid-session), we
  // clear the existing payload so the modal closes immediately.
  const { isChild, isLoading: isSessionLoading } = useIsChildPlayer();

  useEffect(() => {
    if (isChild) {
      setPayload(null);
      return;
    }
    if (isSessionLoading) return;
    return subscribePaywall((next) => {
      setPayload(next);
    });
  }, [isChild, isSessionLoading]);

  function handleOpenChange(open: boolean) {
    if (!open) setPayload(null);
  }

  function handlePrimary() {
    if (!payload) return;
    if (payload.paywallEnabled) {
      setPayload(null);
      setLocation("/pricing");
    }
    // When paywallEnabled is false the button is disabled, so this
    // branch is unreachable — but keep the guard so a future change
    // can't accidentally navigate when checkout isn't live.
  }

  const open = payload !== null;
  const paywallEnabled = payload?.paywallEnabled ?? false;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="paywall-modal"
        aria-labelledby="paywall-modal-title"
        aria-describedby="paywall-modal-body"
        className="max-w-md sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle id="paywall-modal-title">
            {t("paywall.heading")}
          </DialogTitle>
          <DialogDescription id="paywall-modal-body">
            {t("paywall.body")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setPayload(null)}
            data-testid="paywall-modal-secondary"
          >
            {t("paywall.ctaSecondary")}
          </Button>
          <Button
            type="button"
            onClick={handlePrimary}
            disabled={!paywallEnabled}
            data-testid="paywall-modal-primary"
          >
            {paywallEnabled
              ? t("paywall.ctaPrimary")
              : t("checkout.comingSoonCta")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PaywallModal;
